import React, { useState, useMemo } from 'react';
import '../../styles/accessibility.css';

/*
  Bread › Onboarding Guide
  Attractive, structured landing for first‑time users.
  Features:
   - Hero with subtle gradient + trust tagline
   - Role selector (modular, extendable)
   - Adaptive checklist / journey steps per role
   - Quick links & learning tiles
   - ESG / Trust Principles highlight band
   - Lightweight, no external deps beyond existing Bootstrap + Tailwind classes in project
*/

const ROLES = [
  {
    id: 'operations',
    label: 'Operations Manager',
    color: 'from-indigo-600 to-sky-600',
    kpis: ['Engagement Stability', 'Mitigation Effectiveness', 'Incident Flow'],
    steps: [
      'Configure access & roles',
      'Review engagement analytics overview',
      'Activate risk monitoring preferences',
      'Export baseline operational report'
    ]
  },
  {
    id: 'security',
    label: 'Security & Compliance',
    color: 'from-emerald-600 to-teal-600',
    kpis: ['Privacy Score', 'Security Posture', 'Control Coverage'],
    steps: [
      'Validate user provisioning & MFA',
      'Run initial Trust Tech Scan',
      'Examine data privacy assessment',
      'Log first compliance checkpoint'
    ]
  },
  {
    id: 'data',
    label: 'Data / Analytics',
    color: 'from-amber-500 to-orange-600',
    kpis: ['Signal Diversity', 'Experiment Win Rate', 'Segment Quality'],
    steps: [
      'Inspect engagement segments',
      'Review experiment efficiency',
      'Enable ESG overlay toggles',
      'Export synthetic dataset sample'
    ]
  }
];

const QUICK_LINKS = [
  { title: 'Engagement Analytics Overview', path: '/bread/engagement-analytics/overview', icon: 'bi-activity' },
  { title: 'Dashboard Analytics', path: '/bread/engagement-analytics/dashboard-analytics', icon: 'bi-speedometer2' },
  { title: 'Risk Assessment', path: '/bread/risk-assessment', icon: 'bi-shield-lock' },
  { title: 'Trust Tech Scan', path: '/trust-tech-scan/run', icon: 'bi-radar' },
  { title: 'Access Management', path: '/bread/access-management/user-management', icon: 'bi-people' },
  { title: 'Policy & Governance', path: '/operations-management/governance/policies', icon: 'bi-journal-check' }
];

export default function OnboardingGuide(){
  const [role, setRole] = useState(ROLES[0]?.id);
  const active = useMemo(()=> ROLES.find(r=> r.id===role), [role]);
  const [completed, setCompleted] = useState(new Set());
  function toggle(step){
    setCompleted(prev=>{ const n = new Set(prev); n.has(step) ? n.delete(step) : n.add(step); return n; });
  }

  return (
    <div className="min-h-full space-y-10">
      {/* Hero */}
      <section className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-purple-900 via-purple-800 to-fuchsia-700 text-white p-10 shadow-lg">
        <div className="max-w-4xl relative z-10">
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight mb-4">Onboarding Guide</h1>
          <p className="text-white/80 text-lg leading-relaxed mb-6">
            Welcome to the Bread Department. Here, we guide users on what type of ESG data collection exists across teams and markets, and manage access so you have what you need to lead and participate. Every role in the K-R-O Operating System plays a vital note in the score of ethical transformation. This isn't just onboarding, it's alignment. Move in harmony. Rise in orchestration.
          </p>
          <div className="flex flex-wrap gap-3">
            {ROLES.map(r=> (
              <button
                key={r.id}
                onClick={()=> setRole(r.id)}
                className={[
                  'px-4 py-2 rounded-full text-sm font-medium transition-all shadow focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-white/60',
                  role===r.id ? 'bg-white text-purple-800' : 'bg-white/10 hover:bg-white/20 text-white'
                ].join(' ')}
                aria-pressed={role===r.id}
              >{r.label}</button>
            ))}
          </div>
        </div>
        <div className="absolute inset-0 pointer-events-none opacity-30" style={{backgroundImage:'radial-gradient(circle at 20% 30%, rgba(255,255,255,0.25), transparent 60%), radial-gradient(circle at 80% 70%, rgba(255,255,255,0.18), transparent 55%)'}} />
      </section>

      {/* Role Snapshot & Checklist */}
      <section className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          <div className="border border-slate-200 bg-white rounded-xl shadow-sm p-6">
            <h2 className="text-xl font-semibold mb-1 text-slate-800">{active.label} Journey</h2>
            <p className="text-slate-600 text-sm mb-4">Complete the high‑impact activation steps below. Progress auto‑persists in this session.</p>
            <ol className="space-y-3 list-decimal list-inside">
              {active.steps.map(step=>{
                const done = completed.has(step);
                return (
                  <li key={step} className="pl-1">
                    <button
                      onClick={()=> toggle(step)}
                      className={[ 'group w-full text-left flex items-start gap-3 rounded-lg px-3 py-2 transition-colors', done ? 'bg-emerald-50 border border-emerald-200' : 'hover:bg-slate-50 border border-transparent'].join(' ')}
                      aria-pressed={done}
                    >
                      <span className={['mt-0.5 inline-flex h-5 w-5 items-center justify-center rounded-full text-[11px] font-semibold', done ? 'bg-emerald-500 text-white' : 'bg-slate-200 text-slate-600 group-hover:bg-slate-300'].join(' ')}>
                        {done ? '✓' : active.steps.indexOf(step)+1}
                      </span>
                      <span className={['flex-1 text-sm', done ? 'line-through text-emerald-700' : 'text-slate-700'].join(' ')}>{step}</span>
                    </button>
                  </li>
                );
              })}
            </ol>
            <div className="mt-5 flex items-center gap-3 text-sm">
              <div className="flex h-2 rounded bg-slate-200 w-40 overflow-hidden">
                <div className="bg-gradient-to-r from-emerald-500 to-teal-500" style={{width: (completed.size/active.steps.length*100)+'%'}} />
              </div>
              <span className="text-slate-600">{completed.size}/{active.steps.length} complete</span>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-5">
            {active.kpis.map(k=> (
              <div key={k} className="relative rounded-xl border border-slate-200 bg-white p-4 shadow-sm overflow-hidden group">
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity bg-gradient-to-br from-slate-50 to-purple-50" />
                <div className="relative">
                  <p className="text-xs font-medium tracking-wide text-slate-500 uppercase mb-1">Focus KPI</p>
                  <h3 className="text-slate-800 font-semibold text-sm mb-2">{k}</h3>
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center rounded-full bg-slate-100 text-slate-600 text-[11px] px-2 py-1 font-medium">Simulated</span>
                    <span className="inline-flex items-center rounded-full bg-purple-100 text-purple-700 text-[11px] px-2 py-1 font-medium">Track</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Quick Links */}
        <aside className="space-y-6">
          <div className="border border-slate-200 rounded-xl bg-white shadow-sm p-5">
            <h3 className="text-slate-800 font-semibold mb-3 text-sm tracking-wide uppercase">Quick Launch</h3>
            <ul className="space-y-2">
              {QUICK_LINKS.map(link=> (
                <li key={link.path}>
                  <a href={link.path} className="group flex items-center gap-3 rounded-lg px-3 py-2 text-sm text-slate-700 hover:bg-purple-50 hover:text-purple-700 transition-colors">
                    <i className={`bi ${link.icon} text-slate-400 group-hover:text-purple-500`} />
                    <span className="flex-1">{link.title}</span>
                    <span className="text-[10px] text-slate-400 group-hover:text-purple-500">›</span>
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div className="border border-purple-200 rounded-xl bg-gradient-to-br from-purple-50 to-fuchsia-50 p-5 shadow-sm">
            <h3 className="text-purple-800 font-semibold mb-2 text-sm tracking-wide uppercase">Trust & ESG Pillars</h3>
            <ul className="text-xs text-slate-600 space-y-1">
              <li><strong className="text-slate-800">Clarity:</strong> Transparent pathways & benchmark insight toggles.</li>
              <li><strong className="text-slate-800">Integrity:</strong> Unified privacy, security & governance scoring.</li>
              <li><strong className="text-slate-800">Inclusion:</strong> Accessibility & diversity signal surfacing.</li>
              <li><strong className="text-slate-800">Resilience:</strong> Continuous risk heat mapping & mitigation tracking.</li>
            </ul>
          </div>
        </aside>
      </section>

      {/* Learning Tiles */}
      <section>
        <h2 className="text-lg font-semibold text-slate-800 mb-4">Learn the Core Spaces</h2>
        <div className="grid md:grid-cols-3 gap-6">
          <LearningTile title="Engagement Intelligence" desc="Measure behavior, anomalies & real‑time events with ESG overlays." path="/bread/engagement-analytics/overview" gradient="from-indigo-600 to-sky-500" />
          <LearningTile title="Risk & Governance" desc="Track multi‑dimensional risks + mitigation effectiveness." path="/bread/risk-assessment" gradient="from-rose-600 to-orange-500" />
          <LearningTile title="Trust Tech Scan" desc="Run privacy, security & trust scoring assessments." path="/trust-tech-scan/run" gradient="from-emerald-600 to-teal-500" />
        </div>
      </section>

      <footer className="pt-4 pb-10 text-center text-xs text-slate-400">
        © {new Date().getFullYear()} Trust Tech Studio • Onboarding Fabric (Simulated Data)
      </footer>
    </div>
  );
}

function LearningTile({ title, desc, path, gradient }){
  return (
    <a href={path} className="group relative block rounded-2xl overflow-hidden shadow-sm border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-purple-500">
      <div className={`absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity bg-gradient-to-br ${gradient} mix-blend-multiply`} />
      <div className="relative p-6 space-y-3">
        <h3 className="text-slate-800 group-hover:text-white font-semibold tracking-tight text-base transition-colors">{title}</h3>
        <p className="text-slate-600 group-hover:text-white/90 text-sm leading-relaxed">{desc}</p>
        <span className="inline-flex items-center gap-1 text-purple-600 group-hover:text-white text-xs font-medium">Explore <span className="translate-x-0 group-hover:translate-x-0.5 transition-transform">›</span></span>
      </div>
    </a>
  );
}
